import { defineComponent as _defineComponent } from 'vue'
import { IFavoriteLine } from "@/domain/entities/favoriteLine";
import {
  inject,
  onActivated,
  onDeactivated,
  onMounted,
  PropType,
  ref,
} from "vue";
import {
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  ShowErrorModal,
  ShowToastMessage,
} from "@/types";

import { useRemoveFavoriteLine } from "@/composables/line/useRemoveFavoriteLine";
import Modal from "../shared/modal/base/Modal.vue";

import Tabs from "../shared/Tabs.vue";
import FavoriteLineList from "./FavoriteLineList.vue";
import FavoriteBusStopList from "./FavoriteBusStopList.vue";
import { useLineStore } from "@/stores/lineStore";
import { IFavoriteBusStop } from "@/domain/entities/favoriteBusStop";
import { IResultData } from "@/domain/entities/shared/result";
import FullSeatLinesModal from "../shared/modal/FullSeatLinesModal.vue";
import { IFullSeatLine } from "@/domain/entities/lineInfo";
import ScrollTopControl from "../shared/ScrollTopControl.vue";


const __default__ = {
  name: "FavoriteListView",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  favoriteLines: {
    type: Array as PropType<IFavoriteLine[]>,
    default: () => [],
  },
  fullSeatLineRouteCount: {
    type: Number,
    default: 0,
  },
},
  emits: ["onSuccessRemoveFavoriteLine", "updateCurrentFavoriteLine", "onClickBoardingStatusButton"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;

const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;



const emit = __emit;

const scrollWrapper = ref<HTMLElement>();

const emitOnUpdateCurrentFavoriteLine = (line: IFavoriteLine) => {
  emit("updateCurrentFavoriteLine", line);
};

const emitOnClickBoardingStatusButton = (line: IFavoriteLine) => {
  emit("onClickBoardingStatusButton", line);
};

const lineStore = useLineStore();

const shouldShowFavoriteLineRemoveAlert = ref(false);
const toggleShowFavoriteLineRemoveAlert = (show: boolean) => {
  shouldShowFavoriteLineRemoveAlert.value = show;
};

const { favoriteLineToRemove, removeFavoriteLine, updateFavoriteLineToRemove } =
  useRemoveFavoriteLine({
    onSuccess: (result: IResultData<boolean>, lineId: string) => {
      showToastMessage("즐겨찾기가 삭제되었습니다", 2500, 0);
      emit("onSuccessRemoveFavoriteLine", lineId);
    },
    onError: () => {
      showToastMessage("즐겨찾기 삭제에 실패했습니다", 2500, 0);
    },
  });

/** 즐겨찾기 탭 */
const FAVORITE_CATEGORY = ["즐겨찾는 노선", "즐겨찾는 정류장"];
const selectedTabIndex = ref(0);
const updateSelectedTabIndex = (index: number) => {
  selectedTabIndex.value = index;
};

const favoriteBusStops = ref<IFavoriteBusStop[]>();
const filterFavoriteBusStop = (busStopUid: string) => {
  favoriteBusStops.value = favoriteBusStops.value?.filter(
    (busStop) => busStop.uid !== busStopUid
  );
};
/** 즐겨찾기한 정류장 목록을 가져온다 -
 * - CardView 에서는 해당 UI가 없으므로 MainLogin 이 아닌 FavoriteListView에서 호출
 */
const getFavoriteBusStops = async () => {
  const result = await lineStore.getFavoriteBusStops();
  if (result.isSuccess) {
    favoriteBusStops.value = result.data;
    return;
  }

  showErrorModal(
    "즐겨찾기 정류장 오류",
    result.errorMessage,
    result.resultCode,
    result.errorTime
  );
};

/** 만석 노선 리스트 */
const fullSeatLineList = ref<IFullSeatLine[]>([]);
const getFullSeatLineList = async () => {
  const result = await lineStore.getFullSeatLineList();

  if (!result.isSuccess) {
    const { errorMessage, errorTime, resultCode } = result;
    showErrorModal("만석노선 조회 오류", errorMessage, resultCode, errorTime);
    return;
  }

  if (!result.data?.length) {
    showErrorModal("만석노선 조회 오류", "현재 만석 노선이 존재하지 않습니다.");
    return;
  }

  fullSeatLineList.value = result.data ?? [];
};

/** 만선 노선 리스트 팝업 */
const shouldShowFullSeatLines = ref(false);
const toggleShowFullSeatLines = (show: boolean) => {
  shouldShowFullSeatLines.value = show;
};

/** 이벤트 바인딩 - 즐겨찾기 노선 클릭 */
const handleClickRow = (line: IFavoriteLine) => {
  emitOnUpdateCurrentFavoriteLine(line);
};

/** 이벤트 바인딩 - 즐겨찾기 노선 - 별 클릭 */
const handleClickLineFavoriteStatusButton = (line: IFavoriteLine) => {
  updateFavoriteLineToRemove(line);
  toggleShowFavoriteLineRemoveAlert(true);
};

/** 이벤트 바인딩 - 노선 즐겨찾기 해제 확인 모달 OK버튼 */
const handleClickFavoriteLineRemoveAlertOk = async () => {
  if (!favoriteLineToRemove.value) return;

  await removeFavoriteLine(favoriteLineToRemove.value);
  toggleShowFavoriteLineRemoveAlert(false);
};

/** 이벤트 바인딩 - [만석 노선 갯수 표시 버튼] 클릭 */
const handleClickFullSeatLinesButton = async () => {
  await getFullSeatLineList();
  toggleShowFullSeatLines(true);

  if (fullSeatLineList.value.length) {
    toggleShowFullSeatLines(true);
  }
};

const isCalledOnMounted = ref(false);

onMounted(() => {
  isCalledOnMounted.value = true;
  getFavoriteBusStops();
});

onActivated(() => {
  if (isCalledOnMounted.value) return;
  getFavoriteBusStops();
});

onDeactivated(() => {
  isCalledOnMounted.value = false;
});

const __returned__ = { showErrorModal, showToastMessage, emit, scrollWrapper, emitOnUpdateCurrentFavoriteLine, emitOnClickBoardingStatusButton, lineStore, shouldShowFavoriteLineRemoveAlert, toggleShowFavoriteLineRemoveAlert, favoriteLineToRemove, removeFavoriteLine, updateFavoriteLineToRemove, FAVORITE_CATEGORY, selectedTabIndex, updateSelectedTabIndex, favoriteBusStops, filterFavoriteBusStop, getFavoriteBusStops, fullSeatLineList, getFullSeatLineList, shouldShowFullSeatLines, toggleShowFullSeatLines, handleClickRow, handleClickLineFavoriteStatusButton, handleClickFavoriteLineRemoveAlertOk, handleClickFullSeatLinesButton, isCalledOnMounted, Modal, Tabs, FavoriteLineList, FavoriteBusStopList, FullSeatLinesModal, ScrollTopControl }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})